import React, { useEffect, useState, useMemo } from "react";
import {
  TextField,
  Grid,
  Typography,
  Autocomplete,
} from "@mui/material";
import MasterViewController from "../../../redux/actions/masterApiActions";
import { useDispatch, useSelector } from "react-redux";
import EmployerViewController from "../../../redux/actions/EmployerApiAction";
import { getUserInfoFromToken } from "../../../utils/authUtils";
import { buildReportingManagerOptions } from "../../../utils/employeeOptionUtils";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const employeeStatusOptions = [
  { label: "Active", value: "active" },
  { label: "On Leave", value: "on_leave" },
  { label: "Inactive", value: "inactive" },
  { label: "Resigned", value: "resigned" },
];

const employmentTypeOptions = [
  { label: "Full-Time", value: "FT" },
  { label: "Part-Time", value: "PT" },
  { label: "Contract", value: "CT" },
  { label: "Probation", value: "PR" },
  { label: "Work-From-Home", value: "WFH" },
];

const EmploymentDetailsTab = ({ formik }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik;
  const dispatch = useDispatch();
  const { getEmployer, getAllEmployeeEmployer } = EmployerViewController();
  const { employers } = useSelector((state) => state.employer);
  const { getDepartments, getDesignations, getRoles } = MasterViewController();
  const { departments, designations, roles } = useSelector((state) => state.master);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  useEffect(() => {
    dispatch(getDepartments());
    dispatch(getDesignations());
    dispatch(getRoles());
  }, [dispatch]);

  // Admin users have no company_id on token — employer-employee API fails for them.
  useEffect(() => {
    if (!userType) return;
    if (userType === "Admin") {
      dispatch(getEmployer());
    } else {
      dispatch(getAllEmployeeEmployer());
    }
  }, [dispatch, userType]);

  const reportingManagerOptions = useMemo(() => {
    const fromEmployerOnlyApi = userType === "Admin";
    const source =
      fromEmployerOnlyApi && values.company_id
        ? employers.filter(
            (emp) =>
              String(emp.company?.id ?? emp.company) ===
              String(values.company_id)
          )
        : employers;

    return buildReportingManagerOptions(source, {
      fromEmployerOnlyApi,
      roles,
    });
  }, [userType, employers, values.company_id, roles]);

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={departments || []}
          getOptionLabel={(option) => option.name || ""}
          value={departments.find((opt) => opt.id === values.department) || null}
          onChange={(event, newValue) => {
            setFieldValue("department", newValue ? newValue.id : "");
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Department"
              name="department"
              error={touched.department && Boolean(errors.department)}
              helperText={touched.department && errors.department}
            />
          )}
          ListboxProps={{
            style: { maxHeight: 4 * 48, overflowY: "auto" },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={designations || []}
          getOptionLabel={(option) => option.name || ""}
          value={
            designations.find((opt) => opt.id === values.designation) || null
          }
          onChange={(event, newValue) => {
            setFieldValue("designation", newValue ? newValue.id : "");
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Designation"
              name="designation"
              error={touched.designation && Boolean(errors.designation)}
              helperText={touched.designation && errors.designation}
            />
          )}
          ListboxProps={{
            style: { maxHeight: 4 * 48, overflowY: "auto" },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Date of Joining"
          name="joining_date"
          value={values.joining_date}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          InputLabelProps={{ shrink: true }}
          error={touched.joining_date && Boolean(errors.joining_date)}
          helperText={touched.joining_date && errors.joining_date}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={employmentTypeOptions}
          getOptionLabel={(option) => option.label}
          value={
            employmentTypeOptions.find(
              (opt) => opt.value === values.employment_type
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue("employment_type", newValue ? newValue.value : "");
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employment Type"
              name="employment_type"
              required
              error={
                touched.employment_type && Boolean(errors.employment_type)
              }
              helperText={touched.employment_type && errors.employment_type}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Work Location"
          name="work_location"
          value={values.work_location}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={touched.work_location && Boolean(errors.work_location)}
          helperText={touched.work_location && errors.work_location}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={reportingManagerOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            reportingManagerOptions.find(
              (opt) => opt.value === values.reporting_manager
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue("reporting_manager", newValue ? newValue.value : "");
          }}
          onBlur={handleBlur}
          noOptionsText={
            userType === "Admin" && !values.company_id
              ? "Select a company in Basic Information first"
              : "No managers found"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Reporting Manager"
              name="reporting_manager"
              error={
                touched.reporting_manager && Boolean(errors.reporting_manager)
              }
              helperText={
                touched.reporting_manager && errors.reporting_manager
              }
            />
          )}
          ListboxProps={{
            style: { maxHeight: 4 * 48, overflowY: "auto" },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          options={employeeStatusOptions}
          getOptionLabel={(option) => option.label}
          value={
            employeeStatusOptions.find(
              (opt) => opt.value === values.employee_status
            ) || null
          }
          onChange={(event, newValue) => {
            setFieldValue("employee_status", newValue ? newValue.value : "");
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employee Status"
              name="employee_status"
              required
              error={
                touched.employee_status && Boolean(errors.employee_status)
              }
              helperText={touched.employee_status && errors.employee_status}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#1A2B4A" }}
        >
          Job Role & Work Schedule
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Job Description"
          name="job_description"
          value={values.job_description}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.job_description && Boolean(errors.job_description)}
          helperText={touched.job_description && errors.job_description}
          multiline
          rows={3}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Shift Timing"
          name="shift_timing"
          value={values.shift_timing}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.shift_timing && Boolean(errors.shift_timing)}
          helperText={touched.shift_timing && errors.shift_timing}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Working Hours"
          name="working_hours"
          value={values.working_hours}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.working_hours && Boolean(errors.working_hours)}
          helperText={touched.working_hours && errors.working_hours}
        />
      </Grid>

      <Grid item xs={12}>
        <Autocomplete
          multiple
          fullWidth
          options={daysOfWeek}
          value={values.weekly_off || []}
          onChange={(event, newValue) => {
            setFieldValue("weekly_off", newValue);
          }}
          onBlur={handleBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Weekly Off"
              name="weekly_off"
              error={touched.weekly_off && Boolean(errors.weekly_off)}
              helperText={touched.weekly_off && errors.weekly_off}
            />
          )}
          disableCloseOnSelect
        />
      </Grid>
    </Grid>
  );
};

export default EmploymentDetailsTab;
